import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useCreateServiceAccountSecret } from "@/queries/service-account-secret-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useEffect, useId, useState, type FC } from "react";
import type { ServiceAccountHeaders } from "@/api";

type Props = {
    open: boolean;
    onClose: () => void;
    serviceAccountId: string;
    headers: ServiceAccountHeaders;
};

export const ServiceAccountNewSecretModal: FC<Props> = ({ open, onClose, serviceAccountId, headers }) => {
    const { toast } = useToast();
    const createSecret = useCreateServiceAccountSecret({ headers });

    const nameId = useId();
    const secretId = useId();

    const [name, setName] = useState<string>("");
    const [secret, setSecret] = useState<string>("");

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const resetForm = useCallback(() => {
        setName("");
        setSecret("");
    }, []);

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            try {
                await createSecret.mutateAsync({
                    serviceAccountId,
                    secretName: name,
                    plaintextValue: secret,
                    mount: {
                        case: "environmentVariable",
                        value: true,
                    },
                });
                resetForm();
                onClose();
                toast({
                    title: "Secret successfully created",
                    description:
                        "The secret will be available as an environment variable in environments created by this service account.",
                });
            } catch (e) {
                toast({
                    title: "Sorry, we couldn't create your secret",
                    description: formatError(e),
                });
            }
        },
        [serviceAccountId, name, secret, createSecret, onClose, toast, resetForm],
    );

    const isValid = name.length > 0 && secret.length > 0;

    // Reset form when modal opens
    useEffect(() => {
        if (open) {
            resetForm();
        }
    }, [open, resetForm]);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="service-account-new-secret-modal-content"
                data-track-location={TrackLocations.NewSecretModal}
            >
                <DialogHeader>
                    <DialogTitle>New secret</DialogTitle>
                    <DialogDescription>
                        Add a secret that will be available as an environment variable in environments created by this
                        service account.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="overflow-x max-w-full">
                    <ErrorMessage
                        error={createSecret.error}
                        data-testid="error-message-service-account-secret-creation-failed"
                        className="text-center"
                    />
                    <form
                        onSubmit={handleSubmit}
                        id="service-account-new-secret-form"
                        data-testid="service-account-new-secret-form"
                        className="flex flex-col gap-4"
                    >
                        <InputField
                            label="Name"
                            id={nameId}
                            hint="Secret names can only contain alphanumeric characters ([a-z], [A-Z], [0-9]) or underscores (_)."
                        >
                            <Input
                                id={nameId}
                                type="text"
                                name="secret-name"
                                value={name}
                                placeholder="Ex: MY_SECRET"
                                pattern="[a-zA-Z0-9_]+"
                                onChange={(e) => setName(e.target.value)}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                data-testid="service-account-secret-name-input"
                            />
                        </InputField>

                        <InputField id={secretId} label="Secret">
                            <Textarea
                                id={secretId}
                                name="secret-value"
                                rows={5}
                                dontAutosize={true}
                                value={secret}
                                placeholder="Enter your secret"
                                onChange={(e) => setSecret(e.target.value)}
                                className="resize-none"
                                data-testid="service-account-secret-value-input"
                            />
                        </InputField>
                    </form>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="outline">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        variant="primary"
                        form="service-account-new-secret-form"
                        loading={createSecret.isPending}
                        disabled={createSecret.isPending || !isValid}
                        data-testid="service-account-add-secret-submit-button"
                        data-tracking-id="confirm-add-secret-service-account-new-secret-modal"
                    >
                        Add
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
